import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  sitePopulationChartRequestSchema,
  siteTotalPopulationChartResponseSchema,
} from "@situate/contracts/runtime-zod";
import type {
  SitePopulationChartRange,
  SitePopulationChartRequest,
  SiteTotalPopulationChartResponse,
  TotalPopulationTrendUnconfiguredArea,
} from "@situate/contracts";
import type { TimeComparisonLineChartPoint } from "@/shared/charts";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

const SITE_TOTAL_POPULATION_CHART_PATH = "/v1/analytics/site-total-population-chart";
export const SITE_TOTAL_POPULATION_CHART_OPERATION_ID = "analytics_site_total_population_chart";

export interface SiteTotalPopulationChart {
  currentData: TimeComparisonLineChartPoint[];
  snapshotAt: number;
  rangeStart: number;
  rangeEnd: number;
  dataStatus: SiteTotalPopulationChartResponse["data_status"];
  refreshAfterSeconds: number;
  granularity: SiteTotalPopulationChartResponse["granularity"];
  unconfiguredAreas: readonly TotalPopulationTrendUnconfiguredArea[];
}

/**
 * Builds the exact Total Population chart GET path. Area IDs are validated
 * before joining so a malformed ID cannot silently change the requested scope.
 */
export function buildSiteTotalPopulationChartPath(
  range: SitePopulationChartRange,
  areaIds: readonly string[]
): string {
  const canonicalSortedAreaIds = Array.from(new Set(areaIds)).sort();
  const request: SitePopulationChartRequest =
    canonicalSortedAreaIds.length === 0 ? { range } : { range, area_ids: canonicalSortedAreaIds };
  sitePopulationChartRequestSchema.parse(request);

  const params = new URLSearchParams({ range });
  if (canonicalSortedAreaIds.length > 0) {
    params.set("area_ids", canonicalSortedAreaIds.join(","));
  }

  return `${SITE_TOTAL_POPULATION_CHART_PATH}?${params.toString()}`;
}

function toMillis(timestamp: string): number {
  return new Date(timestamp).getTime();
}

export function mapSiteTotalPopulationChartResponse(
  response: SiteTotalPopulationChartResponse
): SiteTotalPopulationChart {
  return {
    currentData: response.series
      .map((point) => ({ timestamp: toMillis(point.sample_at), value: point.population }))
      .sort((a, b) => a.timestamp - b.timestamp),
    snapshotAt: toMillis(response.snapshot_at),
    rangeStart: toMillis(response.range_start),
    rangeEnd: toMillis(response.range_end),
    dataStatus: response.data_status,
    refreshAfterSeconds: response.refresh_after_seconds,
    granularity: response.granularity,
    unconfiguredAreas: response.unconfigured_areas,
  };
}

export function useSiteTotalPopulationChart(
  deploymentId: string,
  areaIds: readonly string[],
  range: SitePopulationChartRange,
  enabled = true
) {
  const runtime = useRuntimeHttpClient();
  const client = runtime.client;
  const canonicalSortedAreaIds = useMemo(() => Array.from(new Set(areaIds)).sort(), [areaIds]);
  const hasGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === SITE_TOTAL_POPULATION_CHART_OPERATION_ID
  );
  const query = useQuery({
    queryKey: ["site-total-population-chart", deploymentId, canonicalSortedAreaIds, range] as const,
    queryFn: async ({ signal }) => {
      const path = buildSiteTotalPopulationChartPath(range, canonicalSortedAreaIds);
      const rawResponse = await client!.get<unknown>(path, { signal });
      const response = siteTotalPopulationChartResponseSchema.parse(rawResponse);
      return mapSiteTotalPopulationChartResponse(response);
    },
    enabled: enabled && Boolean(client) && runtime.deploymentId === deploymentId && hasGrant,
    refetchInterval: (query) =>
      query.state.data === undefined ? false : query.state.data.refreshAfterSeconds * 1000,
  });

  return { ...query, hasGrant };
}
